import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Chip,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { RefreshCw } from 'lucide-react';
import { useFeedbackList } from '../../hooks/useFeedbackList';
import { STATUS_OPTIONS, CATEGORY_OPTIONS, STATUS_COLORS, PRIORITY_COLORS } from '../../constants/feedback';
import { formatDate } from '../../utils/formatDate';
import FeedbackDetailModal from '../../components/admin/FeedbackDetailModal';

export default function FeedbackManagement() {
  const {
    feedbacks,
    loading,
    total,
    page,
    deleteDialog,
    actionLoading,
    detailModal,
    hasActiveFilters,
    totalPages,
    control,
    handleSubmit,
    onFilterSubmit,
    handleClearFilters,
    fetchFeedbacks,
    handlePageChange,
    handleRefresh,
    handleOpenDelete,
    handleCloseDelete,
    handleDelete,
    handleOpenDetail,
    handleCloseDetail,
  } = useFeedbackList();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Feedback Management</h1>
        <Button
          variant="outlined"
          startIcon={<RefreshCw className="w-4 h-4" />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Paper className="p-4">
        <form onSubmit={handleSubmit(onFilterSubmit)}>
          <div className="flex flex-wrap gap-3 items-end">
            {/* Status filter */}
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select {...field} label="Status">
                    <MenuItem value=""><em>All</em></MenuItem>
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            {/* Category filter */}
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Category</InputLabel>
                  <Select {...field} label="Category">
                    <MenuItem value=""><em>All</em></MenuItem>
                    {CATEGORY_OPTIONS.map((c) => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            {/* Date From */}
            <Controller
              name="date_from"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Date From"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
              )}
            />

            {/* Date To */}
            <Controller
              name="date_to"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Date To"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
              )}
            />

            <div className="flex gap-2">
              <Button type="submit" variant="contained">
                Search
              </Button>
            </div>
          </div>

          {hasActiveFilters && (
            <p className="text-xs text-gray-500 mt-2">
              Showing {total} result{total !== 1 ? 's' : ''} with active filters.{' '}
              <button
                type="button"
                onClick={handleClearFilters}
                className="underline text-blue-500 hover:text-blue-700"
              >
                Clear all
              </button>
            </p>
          )}
        </form>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Comments</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 8 }).map((__, i) => (
                    <TableCell key={i}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : feedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No feedback entries found
                </TableCell>
              </TableRow>
            ) : (
              feedbacks.map((fb) => (
                <TableRow key={fb.uuid} hover>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <span
                      className="text-sm font-medium text-gray-900 truncate block cursor-pointer hover:text-blue-600"
                      title={fb.title}
                      onClick={() => handleOpenDetail(fb.uuid)}
                    >
                      {fb.title || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm capitalize text-gray-700">
                      {fb.category?.replace(/_/g, ' ') || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_OPTIONS.find((s) => s.value === fb.status)?.label || fb.status}
                      color={STATUS_COLORS[fb.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {fb.priority ? (
                      <Chip
                        label={fb.priority.charAt(0).toUpperCase() + fb.priority.slice(1)}
                        color={PRIORITY_COLORS[fb.priority] || 'default'}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm text-gray-900">{fb.user_display_name || 'N/A'}</p>
                      <p className="text-xs text-gray-500">{fb.user_email || ''}</p>
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    <span className="text-sm text-gray-700">{fb.comment_count ?? 0}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-700">{formatDate(fb.created_at)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenDetail(fb.uuid)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleOpenDelete(fb)}
                        disabled={actionLoading === fb.uuid}
                      >
                        {actionLoading === fb.uuid ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDelete}>
        <DialogTitle>Delete Feedback</DialogTitle>
        <DialogContent>
          Are you sure you want to permanently delete this feedback entry? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Detail Modal */}
      <FeedbackDetailModal
        open={detailModal.open}
        feedbackUuid={detailModal.uuid}
        onClose={handleCloseDetail}
        onUpdated={fetchFeedbacks}
      />
    </div>
  );
}
